import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  Paper,
  Collapse,
  ListItemButton,
  ListItemIcon,
  Button,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  ArrowBack,
  Search,
  PlayCircleOutline,
  AccountBalance,
  TrendingUp,
  Settings,
  School,
  QuestionAnswer,
  ExpandLess,
  ExpandMore,
  Lightbulb,
  AutoAwesome,
  ShowChart,
  Dashboard,
  ViewList,
} from '@mui/icons-material';
import WelcomeDialog from '../components/WelcomeDialog';

// Help content structure
interface HelpCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  articles: HelpArticle[];
}

interface HelpArticle {
  id: string;
  title: string;
  content: string | React.ReactNode;
  tags?: string[];
  videoUrl?: string;
}

// Define help content
const helpCategories: HelpCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Learn the basics of Trade Advisor',
    icon: <PlayCircleOutline fontSize="large" />,
    articles: [
      {
        id: 'welcome',
        title: 'Welcome to Trade Advisor',
        content: (
          <Box>
            <Typography paragraph>
              Trade Advisor is designed to simplify swing trading by providing intelligent recommendations 
              on when to buy and sell stocks. Our goal is to help you make your money work for you by 
              leveraging market swings.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              What is Swing Trading?
            </Typography>
            <Typography paragraph>
              Swing trading is a strategy that involves holding stocks for several days to weeks, 
              attempting to capture gains from market "swings" or price movements. Unlike day trading, 
              swing trading doesn't require constant monitoring of the markets.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              How Trade Advisor Helps
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="📊 Automated Analysis" secondary="Analyzes technical indicators to identify opportunities" />
              </ListItem>
              <ListItem>
                <ListItemText primary="🎯 Entry & Exit Points" secondary="Recommends when to buy and sell based on your rules" />
              </ListItem>
              <ListItem>
                <ListItemText primary="💼 Portfolio Management" secondary="Tracks your positions and suggests adjustments" />
              </ListItem>
              <ListItem>
                <ListItemText primary="📈 Performance Tracking" secondary="Monitor your trading success over time" />
              </ListItem>
            </List>
          </Box>
        ),
        tags: ['basics', 'introduction'],
      },
      {
        id: 'first-portfolio',
        title: 'Creating Your First Portfolio',
        content: (
          <Box>
            <Typography paragraph>
              A portfolio (or "fund") is where you track your trading positions. Let's set up your first one:
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Step 1: Navigate to Portfolios
            </Typography>
            <Typography paragraph>
              Click on the "Portfolios" tab in the navigation menu.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Step 2: Create New Portfolio
            </Typography>
            <Typography paragraph>
              Click the "Create Portfolio" button and fill in:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Portfolio Name" 
                  secondary="Give it a descriptive name like 'Growth Portfolio' or 'Test Portfolio'" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Initial Fund Size" 
                  secondary="The total amount you plan to invest (e.g., $10,000)" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Max Positions" 
                  secondary="Number of stocks to hold at once (recommended: 4-8 for beginners)" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Portfolio Type" 
                  secondary="Choose 'Real' for actual trading or 'Virtual' for practice" 
                />
              </ListItem>
            </List>
            <Box sx={{ bgcolor: 'info.light', p: 2, borderRadius: 1, mt: 2 }}>
              <Typography variant="body2">
                <strong>💡 Pro Tip:</strong> Start with a Virtual portfolio to practice before using real money!
              </Typography>
            </Box>
          </Box>
        ),
        tags: ['portfolio', 'setup'],
      },
      {
        id: 'building-watchlist',
        title: 'Building Your Watchlist',
        content: (
          <Box>
            <Typography paragraph>
              Your watchlist contains stocks you're monitoring for potential trades. Here's how to build one:
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Adding Stocks Manually
            </Typography>
            <Typography paragraph>
              1. Go to the "Watchlist" tab<br />
              2. Click "Add Stock"<br />
              3. Enter the stock symbol and any known details<br />
              4. Set a pivot price (the price level where you'd consider buying)
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Adding from Shared Content
            </Typography>
            <Typography paragraph>
              Trade Advisor makes it easy to discover stocks from the community:<br />
              1. Go to the "Network" tab<br />
              2. Browse shared watchlists and portfolios<br />
              3. Click on any shared content to view details<br />
              4. Click the "+" button next to any stock to add it to your watchlist
            </Typography>
            <Typography paragraph>
              This is a great way to discover stocks that successful traders are watching or holding.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Bulk Import from MarketSurge
            </Typography>
            <Typography paragraph>
              1. Export your MarketSurge candidates as CSV<br />
              2. Click "Bulk Import" in the Watchlist<br />
              3. Select your CSV file<br />
              4. The app will automatically detect and import the data
            </Typography>
            <Box sx={{ bgcolor: 'warning.light', p: 2, borderRadius: 1, mt: 2 }}>
              <Typography variant="body2">
                <strong>⚠️ Important:</strong> Make sure your CSV includes columns like Symbol, Current Price, 
                and technical indicators for best results. If you are using Market Surge, please use or make a copy 
                of the 'Near Pivot' list. This template includes fields related to Pivot Price, Base Type and Stage.
              </Typography>
            </Box>
          </Box>
        ),
        tags: ['watchlist', 'import', 'network', 'shared'],
      },
      {
        id: 'adding-positions',
        title: 'Adding Positions to Your Portfolio',
        content: (
          <Box>
            <Typography paragraph>
              Once you've identified a promising stock from your watchlist, it's time to add it to your 
              portfolio. Trade Advisor makes this process seamless with the Execute Trade dialog.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Accessing the Execute Trade Dialog
            </Typography>
            <Typography paragraph>
              From the Watchlist screen, click the "Trade" button (shopping cart icon) next to any stock. 
              This opens the Execute Trade dialog where you can record your trades.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Trade Execution Options
            </Typography>
            <Typography paragraph>
              You have two primary options when adding a position:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="1. Already Executed Order" 
                  secondary="Use this if you've already bought the stock through your brokerage. You'll enter the actual execution price and timestamp." 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="2. Pending Limit Order" 
                  secondary="Use this if you've placed a limit order in your brokerage that hasn't filled yet. The position will be marked as 'pending' until you manually execute it." 
                />
              </ListItem>
            </List>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Required Information
            </Typography>
            <Typography paragraph>
              For each trade, you'll need to provide:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Number of Shares" 
                  secondary="How many shares you're buying (the app calculates total cost automatically)" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Execution Price" 
                  secondary="The price per share - either your actual purchase price or limit order price" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Date/Timestamp" 
                  secondary="When the trade was (or will be) executed" 
                />
              </ListItem>
            </List>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Using the Stock Chart
            </Typography>
            <Typography paragraph>
              Before executing a trade, click the chart icon to view:
            </Typography>
            <List>
              <ListItem>• Historical price trends over the last 30 days</ListItem>
              <ListItem>• Volume patterns and spikes</ListItem>
              <ListItem>• Your pivot price marked on the chart</ListItem>
              <ListItem>• Recent price and volume updates</ListItem>
            </List>
            <Typography paragraph>
              This helps you make an informed decision about your entry timing and price.
            </Typography>
            <Box sx={{ bgcolor: 'info.light', p: 2, borderRadius: 1, mt: 2 }}>
              <Typography variant="body2">
                <strong>💡 Pro Tip:</strong> Always double-check your entry price and share count before 
                confirming. Trade Advisor will track your position performance from this baseline.
              </Typography>
            </Box>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              After Adding a Position
            </Typography>
            <Typography paragraph>
              Once added, your position will appear in:
            </Typography>
            <List>
              <ListItem>• Your Portfolio's open positions list</ListItem>
              <ListItem>• The Dashboard overview</ListItem>
              <ListItem>• The Recommendations engine for exit timing</ListItem>
            </List>
            <Typography paragraph>
              Trade Advisor will automatically track your position's performance and alert you when 
              it's time to consider selling based on your trading rules.
            </Typography>
          </Box>
        ),
        tags: ['trading', 'positions', 'portfolio'],
      },
    ],
  },
  {
    id: 'features',
    title: 'Features Guide',
    description: 'Deep dive into Trade Advisor features',
    icon: <Dashboard fontSize="large" />,
    articles: [
      {
        id: 'dashboard',
        title: 'Dashboard Overview',
        content: (
          <Box>
            <Typography paragraph>
              The Dashboard is your command center, showing a real-time overview of your trading activity.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Key Metrics
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Total Portfolio Value" 
                  secondary="Combined value of all your portfolios" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Total Return" 
                  secondary="Your overall profit/loss percentage" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Win Rate" 
                  secondary="Percentage of profitable trades" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Open Positions" 
                  secondary="Number of active trades across all portfolios" 
                />
              </ListItem>
            </List>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Recent Activity
            </Typography>
            <Typography paragraph>
              View your latest trades, recommendations, and portfolio changes at a glance.
            </Typography>
          </Box>
        ),
        tags: ['dashboard', 'overview'],
      },
      {
        id: 'recommendations',
        title: 'Understanding Recommendations',
        content: (
          <Box>
            <Typography paragraph>
              The Recommendations engine is the heart of Trade Advisor, analyzing your watchlist and 
              portfolios to suggest optimal trading actions.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Types of Recommendations
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Entry (Buy) Recommendations" 
                  secondary="Stocks showing strong technical setup for entry" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Exit (Sell) Recommendations" 
                  secondary="Positions that should be closed based on targets or stops" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Adjustment Recommendations" 
                  secondary="Suggestions to raise stops or adjust targets" 
                />
              </ListItem>
            </List>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Recommendation Factors
            </Typography>
            <Typography paragraph>
              Our algorithm considers:
            </Typography>
            <List>
              <ListItem>• Distance from pivot price</ListItem>
              <ListItem>• Volume surge indicators</ListItem>
              <ListItem>• Base pattern formation</ListItem>
              <ListItem>• Portfolio capacity and available funds</ListItem>
              <ListItem>• Your custom trading rules</ListItem>
            </List>
            <Box sx={{ bgcolor: 'success.light', p: 2, borderRadius: 1, mt: 2 }}>
              <Typography variant="body2">
                <strong>✅ Quick Actions:</strong> Each recommendation includes direct action buttons 
                to execute trades immediately.
              </Typography>
            </Box>
          </Box>
        ),
        tags: ['recommendations', 'trading'],
      },
      {
        id: 'trading-rules',
        title: 'Configuring Trading Rules',
        content: (
          <Box>
            <Typography paragraph>
              Trading rules define how the recommendation engine makes decisions for your portfolios.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Key Rules to Configure
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Max Holding Period" 
                  secondary="Maximum days to hold any position (e.g., 21 days)" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Stop Loss %" 
                  secondary="Automatic sell if stock drops this percentage (e.g., 5%)" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Target Gain %" 
                  secondary="Take profit when stock rises this percentage (e.g., 10%)" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Max Distance from Pivot" 
                  secondary="Only buy stocks within this % of pivot price (e.g., 3%)" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Min Volume Increase" 
                  secondary="Require volume surge above average (e.g., 50%)" 
                />
              </ListItem>
            </List>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Portfolio-Specific Rules
            </Typography>
            <Typography paragraph>
              You can set different rules for each portfolio, allowing you to test various strategies.
            </Typography>
          </Box>
        ),
        tags: ['rules', 'configuration'],
      },
      {
        id: 'autopilot',
        title: 'Using Autopilot Mode',
        content: (
          <Box>
            <Typography paragraph>
              Autopilot automatically executes all recommendations for virtual portfolios, 
              perfect for strategy testing.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Enabling Autopilot
            </Typography>
            <Typography paragraph>
              1. Navigate to your virtual portfolio<br />
              2. Click the "Enable Autopilot" toggle<br />
              3. Confirm your trading rules are configured<br />
              4. Autopilot will execute recommendations automatically
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              What Autopilot Does
            </Typography>
            <List>
              <ListItem>• Buys recommended stocks at market open</ListItem>
              <ListItem>• Sells when exit conditions are met</ListItem>
              <ListItem>• Adjusts stops and targets automatically</ListItem>
              <ListItem>• Maintains portfolio balance</ListItem>
            </List>
            <Box sx={{ bgcolor: 'warning.light', p: 2, borderRadius: 1, mt: 2 }}>
              <Typography variant="body2">
                <strong>⚠️ Note:</strong> Autopilot is only available for virtual portfolios. 
                Real portfolios require manual trade execution.
              </Typography>
            </Box>
          </Box>
        ),
        tags: ['autopilot', 'automation'],
      },
    ],
  },
  {
    id: 'trading-concepts',
    title: 'Trading Concepts',
    description: 'Learn key trading terminology and strategies',
    icon: <School fontSize="large" />,
    articles: [
      {
        id: 'pivot-price',
        title: 'Understanding Pivot Price',
        content: (
          <Box>
            <Typography paragraph>
              The pivot price is a key technical level where a stock is likely to find support 
              or begin a new upward move.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              How to Identify Pivot Points
            </Typography>
            <Typography paragraph>
              • Look for consolidation areas where price has traded sideways<br />
              • Identify the high point of the consolidation (the pivot)<br />
              • Best entries occur when price breaks above this level on volume
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Using Pivots in Trade Advisor
            </Typography>
            <Typography paragraph>
              Set pivot prices for your watchlist stocks. The app will alert you when 
              stocks approach these key levels, maximizing your entry timing.
            </Typography>
          </Box>
        ),
        tags: ['pivot', 'technical analysis'],
      },
      {
        id: 'volume-spike',
        title: 'Volume Spike Indicator',
        content: (
          <Box>
            <Typography paragraph>
              Volume spikes indicate unusual trading activity and often precede significant price moves.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              How We Calculate Volume Spikes
            </Typography>
            <Typography paragraph>
              Our algorithm compares current volume to the average daily volume, 
              adjusted for the time of day. A reading of 150% at 50% through the trading day 
              means volume is running at 1.5x the normal pace.
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Trading on Volume
            </Typography>
            <List>
              <ListItem>• Look for spikes above 150% for potential breakouts</ListItem>
              <ListItem>• Combine with price action near pivot points</ListItem>
              <ListItem>• Higher volume = stronger conviction in the move</ListItem>
            </List>
          </Box>
        ),
        tags: ['volume', 'indicators'],
      },
    ],
  },
  {
    id: 'faq',
    title: 'FAQ',
    description: 'Frequently asked questions',
    icon: <QuestionAnswer fontSize="large" />,
    articles: [
      {
        id: 'faq-general',
        title: 'Frequently Asked Questions',
        content: (
          <Box>
            <Typography variant="h6" gutterBottom>
              Is Trade Advisor suitable for beginners?
            </Typography>
            <Typography paragraph>
              Yes! Start with a virtual portfolio to learn without risk. Our recommendations 
              guide you through each trade decision.
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              How often should I check the app?
            </Typography>
            <Typography paragraph>
              For swing trading, checking once or twice daily is sufficient. 
              Set up notifications for important alerts.
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Can I connect my brokerage account?
            </Typography>
            <Typography paragraph>
              Currently, Trade Advisor doesn't directly connect to brokerages. 
              You'll need to manually execute trades in your brokerage app.
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              How accurate are the recommendations?
            </Typography>
            <Typography paragraph>
              No trading system is 100% accurate. Our recommendations are based on 
              technical analysis and your rules. Always use stop losses to manage risk.
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              What data sources do you use?
            </Typography>
            <Typography paragraph>
              We use multiple data providers including Finnhub for real-time quotes 
              and support imports from MarketSurge for technical indicators.
            </Typography>
          </Box>
        ),
        tags: ['faq'],
      },
    ],
  },
];

const Help: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [showWelcome, setShowWelcome] = useState(false);

  const handleCategoryClick = (category: HelpCategory) => {
    setSelectedCategory(category);
    setSelectedArticle(null);
  };

  const handleArticleClick = (article: HelpArticle) => {
    setSelectedArticle(article);
  };

  const handleBack = () => {
    if (selectedArticle) {
      setSelectedArticle(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Filter articles based on search
  const filteredCategories = helpCategories.map(category => ({
    ...category,
    articles: category.articles.filter(
      article =>
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (typeof article.content === 'string' && 
          article.content.toLowerCase().includes(searchQuery.toLowerCase())) ||
        article.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    ),
  })).filter(category => 
    category.articles.length > 0 || 
    category.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render main help center
  if (!selectedCategory && !selectedArticle) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Typography variant="h3" gutterBottom align="center">
            Help Center
          </Typography>
          <Typography variant="h6" color="textSecondary" align="center" paragraph>
            Everything you need to know about using Trade Advisor
          </Typography>

          <Box sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<PlayCircleOutline />}
              onClick={() => setShowWelcome(true)}
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              Show Getting Started Guide
            </Button>
          </Box>

          <Grid container spacing={3}>
            {filteredCategories.map((category) => (
              <Grid item xs={12} sm={6} md={3} key={category.id}>
                <Card sx={{ height: '100%' }}>
                  <CardActionArea 
                    sx={{ height: '100%', p: 2 }}
                    onClick={() => handleCategoryClick(category)}
                  >
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ color: 'primary.main', mb: 2 }}>
                        {category.icon}
                      </Box>
                      <Typography variant="h6" gutterBottom>
                        {category.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {category.description}
                      </Typography>
                      <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                        {category.articles.length} articles
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 6, p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom>
              Need More Help?
            </Typography>
            <Typography paragraph>
              Can't find what you're looking for? Here are some additional resources:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Lightbulb />}
                  href="#tips"
                >
                  Trading Tips
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ShowChart />}
                  href="#strategies"
                >
                  Strategy Guides
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ViewList />}
                  href="#glossary"
                >
                  Trading Glossary
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <WelcomeDialog 
          forceOpen={showWelcome} 
          onForceClose={() => setShowWelcome(false)} 
        />
      </Container>
    );
  }

  // Render category view
  if (selectedCategory && !selectedArticle) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Box sx={{ mb: 3 }}>
            <IconButton onClick={handleBack} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Breadcrumbs>
              <Link 
                underline="hover" 
                color="inherit" 
                href="#" 
                onClick={(e) => { e.preventDefault(); setSelectedCategory(null); }}
              >
                Help Center
              </Link>
              <Typography color="textPrimary">{selectedCategory.title}</Typography>
            </Breadcrumbs>
          </Box>

          <Typography variant="h4" gutterBottom>
            {selectedCategory.title}
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            {selectedCategory.description}
          </Typography>

          <List>
            {selectedCategory.articles.map((article) => (
              <Paper key={article.id} sx={{ mb: 1 }}>
                <ListItemButton onClick={() => handleArticleClick(article)}>
                  <ListItemText
                    primary={article.title}
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        {article.tags?.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{ mr: 0.5 }}
                          />
                        ))}
                      </Box>
                    }
                  />
                </ListItemButton>
              </Paper>
            ))}
          </List>
        </Box>
      </Container>
    );
  }

  // Render article view
  if (selectedArticle) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Box sx={{ mb: 3 }}>
            <IconButton onClick={handleBack} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Breadcrumbs>
              <Link 
                underline="hover" 
                color="inherit" 
                href="#" 
                onClick={(e) => { e.preventDefault(); setSelectedCategory(null); setSelectedArticle(null); }}
              >
                Help Center
              </Link>
              <Link
                underline="hover"
                color="inherit"
                href="#"
                onClick={(e) => { e.preventDefault(); setSelectedArticle(null); }}
              >
                {selectedCategory?.title}
              </Link>
              <Typography color="textPrimary">{selectedArticle.title}</Typography>
            </Breadcrumbs>
          </Box>

          <Typography variant="h4" gutterBottom>
            {selectedArticle.title}
          </Typography>

          {selectedArticle.tags && (
            <Box sx={{ mb: 3 }}>
              {selectedArticle.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ mr: 0.5 }}
                />
              ))}
            </Box>
          )}

          <Paper sx={{ p: 3 }}>
            {typeof selectedArticle.content === 'string' ? (
              <Typography>{selectedArticle.content}</Typography>
            ) : (
              selectedArticle.content
            )}

            {selectedArticle.videoUrl && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Video Tutorial
                </Typography>
                {/* Add video embed here */}
              </Box>
            )}
          </Paper>
        </Box>
      </Container>
    );
  }

  return null;
};

export default Help;